'use client';
import { useMicrocopy } from '@lib/cms/microcopy/client';
import { useCookieSettings } from '@lib/hooks/useCookieSettings';
import {
  ContentSection as ContentSectionType,
  TextAndMediaItem,
} from '@lib/types/cms/ContentSection';
import { toNonNullable } from '@lib/util/toNonNullable';
import { ImageFocalPoint } from '@ui-components/lib/types';
import { useEffect, useState } from 'react';
import { ButtonStyle, MediaAlignment, TextAndMedia } from 'ui-components';

type ContentSectionProps = {
  data: ContentSectionType;
};

const getMediaAlignment = (
  currentModulesWithMediaInARow: number,
  module: TextAndMediaItem
): MediaAlignment => {
  const hasImage = !!module?.image;
  const hasVideo = module?.videoUrl !== null;
  const hasMedia = hasImage || hasVideo;
  let mediaAlignment: MediaAlignment = 'right';

  if (hasMedia) {
    const EVEN_ODD_CHECKER = 2;
    const isOdd = currentModulesWithMediaInARow % EVEN_ODD_CHECKER;
    mediaAlignment = isOdd ? 'left' : 'right';
  }

  return mediaAlignment;
};

const RenderTextAndMedia = (
  module: TextAndMediaItem,
  mediaAlignment: MediaAlignment,
  mediaFocalPoint: ImageFocalPoint | null,
  videoEmbed: string | null,
  cookieConsent: boolean,
  openCookieSettings: () => void
) => {
  const microcopy = useMicrocopy('video', {
    'video.cookie-overlay-description':
      'This content is hosted by a third party provider that does not allow video views without acceptance of Targeting and Functional Cookies. Please set your cookie preferences for Targeting and Functional Cookies to yes if you wish to view videos from these providers.',
    'video.cookie-overlay-button-label': 'Open Cookie Settings',
  });
  if (!module?.body) {
    return null;
  }

  // Guard against stale Redis-cached pages transformed before `ctas` existed:
  // those cached blobs have no `ctas` key, so `module.ctas` can be undefined.
  const actions = (module.ctas ?? []).map((cta) => ({
    label: cta.label,
    url: cta.url,
    openInNewTab: toNonNullable(cta.openInNewTab),
    style:
      cta.appearance === 'primary'
        ? ('primary' as ButtonStyle)
        : ('secondary' as ButtonStyle),
  }));

  return (
    <TextAndMedia
      key={module.title}
      title={module.title}
      text={module.body}
      image={{
        url: module.image?.url ?? '',
        alt: module.image?.alt ?? '',
        height: toNonNullable(module.image?.height),
        width: toNonNullable(module.image?.width),
      }}
      imageFocusArea={mediaFocalPoint}
      mediaAlignment={mediaAlignment}
      actions={actions}
      video={
        module?.videoUrl
          ? {
              title: module.videoTitle ?? '',
              embedUrl: videoEmbed ?? '',
              cookieConsent,
            }
          : null
      }
      className="mb-14"
      microcopy={{
        videoCookieOverlayMicrocopy: {
          description: microcopy['video.cookie-overlay-description'],
          buttonLabel: microcopy['video.cookie-overlay-button-label'],
        },
      }}
      onOpenCookieSettings={openCookieSettings}
    />
  );
};

const ContentSection = (props: ContentSectionProps) => {
  const { items } = props.data;
  const [host, setHost] = useState<string | null>(null);
  const { hasCookieConsent, openCookieSettings } = useCookieSettings();
  let mediaAlignment: MediaAlignment = 'right';
  let currentModulesWithMediaInARow = 0;

  useEffect(() => {
    setHost(window.location.host);
  }, []);

  return items
    .filter((module) => module?.body)
    .map((module) => {
      if (!module?.body) {
        return null;
      }

      let videoEmbed = module?.videoUrl ?? null;

      // add location host to twitch embed url
      if (module?.videoProvider === 'twitch') {
        videoEmbed = `${module.videoUrl}&parent=${host}&autoplay=false`;
      }

      if (module?.image || module?.videoUrl) {
        mediaAlignment = getMediaAlignment(
          currentModulesWithMediaInARow,
          module
        );

        currentModulesWithMediaInARow++;
      }

      return RenderTextAndMedia(
        module,
        mediaAlignment,
        module.imageFocusArea ?? null,
        videoEmbed,
        hasCookieConsent,
        openCookieSettings
      );
    });
};

export default ContentSection;
